import type {
  ActionKind,
  AuftragSperrgrund,
  BeauftragtePosition,
  BefundAktionInput,
  BefundAufsicht,
  BefundErledigtListe,
  BefundListe,
  BefundLocale,
  BefundPruefungEinstellungen,
  BefundRegelAdmin,
  BefundRegelKey,
  BefundRegelnHilfe,
  BefundUebersicht,
  BefundVerlauf,
  BesetzungView,
  CreateStaffingBesetzung,
  Datenstand,
  DienstStatus,
  DienstTestErgebnis,
  EntraAbgleichEinstellungen,
  FeatureFlag,
  FunctionRole,
  HandoverReadyFields,
  IntakeHandoverFields,
  KfmlTodoList,
  KfmlWorklistResponse,
  KiBriefing,
  KlStaffingBoardResponse,
  NeuerNutzer,
  NewsEntwurf,
  NewsGelesenResponse,
  NewsListeResponse,
  NewsUebersetzenRequest,
  NewsUebersetzenResponse,
  NotificationCadence,
  NotificationChannel,
  NotificationEventType,
  NotificationLocale,
  NotificationRecipientRole,
  PatchStaffingBesetzung,
  Persona,
  PersonasResponse,
  Phase,
  PhaseDueMeaning,
  PhaseHistory,
  PlannerImportCommitResponse,
  PlannerImportPreviewResponse,
  PlannerImportRunDetail,
  PlannerImportRunSummary,
  PreisEinheit,
  ProduktNews,
  Project,
  ProjectAttachment,
  PromptListItem,
  Responsibility,
  Segment,
  SegmentNote,
  SegmentNoteImageUpload,
  SfFileListItem,
  StaffingBesetzung,
  StaffingComment,
  StaffingDetails,
  StaffingMaske,
  SyncRun,
  SyncSchedule,
  SyncScheduleState,
  Tag,
  Task,
  UpdateBefundPruefungEinstellungenInput,
  UpdateEntraAbgleichZeitplanInput,
  User,
  UserPreferences,
} from '@projektverwaltung/shared';
import { getSessionUserId } from './session.js';

export type DevUser = {
  user_id: string;
  name: string;
  email: string;
  is_admin: boolean;
  is_test_user: boolean;
  function_roles: { role: FunctionRole; scope_segment: Segment | null }[];
};

export type AdminUser = User & {
  is_test_user: boolean;
  function_roles: {
    user_function_role_id: string;
    user_id: string;
    role: FunctionRole;
    scope_segment: Segment | null;
    created_at: string;
  }[];
  /** F1 Task 2: true, wenn der User die Rolle `projektleiter` nur automatisch
   * (über `project_lead_user_id`) hat — keine manuelle Zeile in `function_roles`. */
  is_auto_pl: boolean;
};

export type HardStopViolation = {
  field: string;
  reason: 'missing' | 'invalid';
  direction: 'leave' | 'enter';
};

export class ApiError extends Error {
  status: number;
  payload: unknown;
  constructor(message: string, status: number, payload: unknown) {
    super(message);
    this.status = status;
    this.payload = payload;
  }
  isHardStop(): this is ApiError & {
    payload: { errors: HardStopViolation[]; from_phase: string | null; to_phase: string };
  } {
    return (
      this.status === 409 &&
      typeof this.payload === 'object' &&
      this.payload !== null &&
      (this.payload as { message?: string }).message === 'hard_stop_violation'
    );
  }
}

export type SfTestResult =
  | {
      ok: true;
      instanceUrl: string;
      userId?: string;
      organizationId?: string;
      apiVersion: string;
      projectObject: string;
      fieldMap: Record<string, string | undefined>;
    }
  | { ok: false; reason: string };

export type SyncTriggerResult = {
  sync_run_id: string;
  status: 'success' | 'partial' | 'failure';
  counts: {
    fetched: number;
    created: number;
    updated: number;
    skipped: number;
    skipped_segment: number;
    skipped_no_segment: number;
    errors: number;
    // PV-052 L3: fehlgeschlagene Ist-Abfragen; fehlt ohne Ist-Abgleich (Delta).
    ist_fehler?: number;
    // #131: nur im Refresh — angefragte Keys, die Salesforce nicht zurückgab.
    sf_geloescht?: number;
    sf_nicht_auffindbar?: number;
    sf_vorhanden_anderer_key?: number;
    sf_pruef_fehler?: number;
  };
  errors: { salesforce_object_id: string; reason: string }[];
};

const BASE = import.meta.env.VITE_API_BASE_URL ?? '/api';

/** Voll-Redirect-URL zum Azure-Login (Muster A startet serverseitig). `next` = Ruecksprungziel. */
export function authLoginUrl(next?: string): string {
  const q = next && next !== '/' ? `?next=${encodeURIComponent(next)}` : '';
  return `${BASE}/v1/auth/login${q}`;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const sessionUserId = getSessionUserId();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...((init?.headers as Record<string, string>) ?? {}),
  };
  if (sessionUserId) headers['x-dev-user-id'] = sessionUserId;
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    headers,
    credentials: 'include', // Muster A: httpOnly-Session-Cookie mitsenden
  });
  if (!res.ok) {
    // Session abgelaufen/ungültig: Das httpOnly-Cookie (8h TTL) kann längst weg
    // sein, während der localStorage-Cache (pv.session) den User noch als
    // "eingeloggt" führt → jeder Call 401, lautlos. Cache verwerfen + zum Login
    // leiten, statt stumm zu scheitern. /v1/auth/me ist die erwartete
    // Hydrierungs-Probe (session.tsx) und darf NICHT umleiten (sonst Loop).
    if (res.status === 401 && !path.startsWith('/v1/auth/me')) {
      try {
        localStorage.removeItem('pv.session');
      } catch {
        /* localStorage evtl. nicht verfügbar */
      }
      if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
        window.location.assign('/login');
      }
    }
    let detail = res.statusText;
    let payload: unknown = null;
    try {
      payload = await res.json();
      const body = payload as { error?: string; message?: string };
      detail = body.message ?? body.error ?? detail;
    } catch {
      // ignore non-JSON body
    }
    throw new ApiError(detail || `API ${res.status}`, res.status, payload);
  }
  // 204 hat keinen Körper — `res.json()` würfe dort (z. B. „Geprüft“ bei Neue Nutzer, #116).
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export type ListProjectsFilter = {
  segment?: string;
  q?: string;
  staffingNeeded?: boolean;
  projectLeadUserId?: string;
  dueFrom?: string;
  dueTo?: string;
  overdueOnly?: boolean;
};

function buildQuery(filter: ListProjectsFilter): string {
  const params = new URLSearchParams();
  if (filter.segment) params.set('segment', filter.segment);
  if (filter.q) params.set('q', filter.q);
  if (filter.staffingNeeded) params.set('staffing_needed', 'true');
  if (filter.projectLeadUserId) params.set('project_lead_user_id', filter.projectLeadUserId);
  if (filter.dueFrom) params.set('due_from', filter.dueFrom);
  if (filter.dueTo) params.set('due_to', filter.dueTo);
  if (filter.overdueOnly) params.set('overdue', 'true');
  const s = params.toString();
  return s ? `?${s}` : '';
}

/** Anzeige-Zustand der Orakel-Verbindung (PV-045). */
export type McpStatusValue = 'connected' | 'unavailable' | 'revoked' | 'none';

export const api = {
  listProjects: (filter: ListProjectsFilter = {}) =>
    request<{ items: Project[] }>(`/v1/projects${buildQuery(filter)}`),
  getProject: (id: string) => request<Project>(`/v1/projects/${id}`),

  // Deeplink (Issue #3): SF-Object-Id ODER project_key (P\d{6}) → interne project_id.
  resolveProjectRef: (ref: string) =>
    request<{ project_id: string }>(`/v1/projects/resolve/${encodeURIComponent(ref)}`),
  // V1.5: alle Aufträge eines Projekts (Tab "Aufträge & CRs")
  listAuftraegeForProject: (id: string) =>
    request<{ items: Array<Record<string, unknown>>; leading_auftrag_id: string }>(
      `/v1/projects/${id}/auftraege`,
    ),
  // V1.5: bewegliche Aufträge für Add-Modal (Brief §551-555)
  listMovableAuftraege: (params: { q?: string; excludeProjectId?: string } = {}) => {
    const u = new URLSearchParams();
    if (params.q) u.set('q', params.q);
    if (params.excludeProjectId) u.set('exclude_project_id', params.excludeProjectId);
    const qs = u.toString();
    return request<{
      items: Array<{
        auftrag_id: string;
        project_key: string;
        name: string;
        customer_name: string;
        segment: string;
        kind: 'auftrag' | 'cr';
        ms_classification: string | null;
        sf_budget_eur: string | null;
        source_project_id: string;
        source_project_key: string;
        source_member_count: number;
        sperrgruende: AuftragSperrgrund[];
      }>;
    }>(`/v1/auftraege/movable${qs ? `?${qs}` : ''}`);
  },
  attachAuftrag: (targetProjectId: string, auftragId: string) =>
    request<{ project: Project; source_merged: boolean }>(
      `/v1/projects/${targetProjectId}/auftraege/attach`,
      { method: 'POST', body: JSON.stringify({ auftrag_id: auftragId }) },
    ),
  detachAuftrag: (projectId: string, auftragId: string) =>
    // #123: `wiederhergestellt` = ein zusammengeführtes Ur-Projekt lebte wieder auf (dann ist
    // `new_project` DAS), sonst ist `new_project` ein neues Solo-Projekt.
    request<{ new_project: Project; wiederhergestellt: boolean }>(
      `/v1/projects/${projectId}/auftraege/${auftragId}/detach`,
      { method: 'POST' },
    ),
  setLeadingAuftrag: (projectId: string, auftragId: string) =>
    request<{ project: Project }>(`/v1/projects/${projectId}/auftraege/${auftragId}/set-leading`, {
      method: 'POST',
    }),
  /** #78 — ausstehende Salesforce-Uebertragung verwerfen; danach gilt wieder der SF-Stand. */
  discardSfWrite: (projectId: string, topic: string) =>
    request<{ ok: true; topic: string }>(`/v1/projects/${projectId}/sf-write/${topic}`, {
      method: 'DELETE',
    }),
  patchProject: (
    id: string,
    input: {
      staffing_details?: StaffingDetails | null;
      handover_ready_fields?: HandoverReadyFields | null;
      bereich?: string | null;
      project_lead_user_id?: string | null;
      manager_user_id?: string | null;
      mtb_user_id?: string | null;
      responsibility?: Responsibility | null;
      due_date?: string | null;
      kl_followup_date?: string | null;
      intake_handover_fields?: IntakeHandoverFields | null;
    },
  ) => request<Project>(`/v1/projects/${id}`, { method: 'PATCH', body: JSON.stringify(input) }),
  /** #158: Übergabe-Notiz (`uebergabe_intake.sonstiges`) — schreibt NUR dieses Feld. */
  setUebergabeNotiz: (id: string, sonstiges: string | null) =>
    request<{ sonstiges: string | null }>(`/v1/projects/${id}/uebergabe-notiz`, {
      method: 'PATCH',
      body: JSON.stringify({ sonstiges }),
    }),
  setKfmlWorklist: (id: string, reason?: string) =>
    request<Project>(`/v1/projects/${id}/kfml-worklist-flag`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),
  clearKfmlWorklist: (id: string, reason?: string) =>
    request<Project>(`/v1/projects/${id}/kfml-worklist-resolve`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),
  // PV-052 L2: „final geschlossen" von Hand (KFML/Admin).
  setFinal: (id: string, grund: string) =>
    request<Project>(`/v1/projects/${id}/final`, {
      method: 'POST',
      body: JSON.stringify({ grund }),
    }),
  clearFinal: (id: string) => request<Project>(`/v1/projects/${id}/final`, { method: 'DELETE' }),
  listUsers: (q?: string) =>
    request<{ items: User[] }>(`/v1/users${q ? `?q=${encodeURIComponent(q)}` : ''}`),
  getMyPersonas: () => request<PersonasResponse>('/v1/users/me/personas'),
  getMyPreferences: () => request<{ preferences: UserPreferences }>('/v1/users/me/preferences'),
  updateMyPreferences: (patch: UserPreferences) =>
    request<{ preferences: UserPreferences }>('/v1/users/me/preferences', {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),
  setActivePersona: (persona: Persona) =>
    request<{ preferences: UserPreferences }>('/v1/users/me/preferences', {
      method: 'PATCH',
      body: JSON.stringify({ active_persona: persona }),
    }),
  listTasks: (projectId: string) =>
    request<{ items: Task[] }>(`/v1/tasks?project_id=${encodeURIComponent(projectId)}`),
  createTask: (input: {
    project_id: string;
    title: string;
    owner_user_id?: string | null;
    status?: 'open' | 'in_progress' | 'done';
    due_date?: string | null;
    description?: string | null;
  }) => request<Task>('/v1/tasks', { method: 'POST', body: JSON.stringify(input) }),
  patchTask: (
    id: string,
    input: {
      title?: string;
      owner_user_id?: string | null;
      status?: 'open' | 'in_progress' | 'done';
      due_date?: string | null;
      description?: string | null;
    },
  ) => request<Task>(`/v1/tasks/${id}`, { method: 'PATCH', body: JSON.stringify(input) }),
  deleteTask: (id: string) => request<{ deleted: string }>(`/v1/tasks/${id}`, { method: 'DELETE' }),
  movePhase: (projectId: string, toPhaseId: string) =>
    request<Project>(`/v1/projects/${projectId}/move-phase`, {
      method: 'POST',
      body: JSON.stringify({ to_phase_id: toPhaseId }),
    }),
  // `gate` (#61): der Phasenwechsel, der Admin/Kaufm. Leitung vorbehalten ist.
  // Reine Anzeige-Information — durchgesetzt wird er im Backend.
  listPhases: () =>
    request<{ items: Phase[]; gate?: { from_phase: string; to_phase: string } }>('/v1/phases'),
  createPhase: (input: {
    name: string;
    order: number;
    description?: string;
    due_meaning?: PhaseDueMeaning;
  }) => request<Phase>('/v1/phases', { method: 'POST', body: JSON.stringify(input) }),
  updatePhase: (
    id: string,
    input: { name?: string; order?: number; description?: string; due_meaning?: PhaseDueMeaning },
  ) => request<Phase>(`/v1/phases/${id}`, { method: 'PATCH', body: JSON.stringify(input) }),
  deletePhase: (id: string) =>
    request<{ deleted: string }>(`/v1/phases/${id}`, { method: 'DELETE' }),
  projectHistory: (id: string) => request<{ items: PhaseHistory[] }>(`/v1/projects/${id}/history`),
  listRecentActivity: (params: { since_hours?: number; limit?: number } = {}) => {
    const q = new URLSearchParams();
    if (params.since_hours !== undefined) q.set('since_hours', String(params.since_hours));
    if (params.limit !== undefined) q.set('limit', String(params.limit));
    const s = q.toString();
    return request<{
      items: Array<{
        history_id: string;
        project_id: string;
        project_key: string;
        customer_name: string;
        event_type:
          | 'phase_change'
          | 'lifecycle_completed'
          | 'lifecycle_reactivated'
          | 'qs_with_bl_flagged'
          | 'qs_with_bl_resolved'
          | 'blocked_flagged'
          | 'blocked_resolved'
          | 'staffing_request_created'
          | 'staffing_request_resolved'
          | 'responsibility_assigned'
          | 'handover_field_completed'
          | 'auftrag_merged_into_project'
          | 'auftrag_split_from_project'
          | 'sync_refresh'
          | 'mention_mail_sent'
          | 'projekt_zusammengefuehrt'
          | 'projekt_wiederhergestellt'
          // #131
          | 'auftrag_sf_geloescht'
          | 'auftrag_sf_wiederhergestellt'
          | 'sf_uebertragung_verworfen';
        note: string | null;
        changed_at: string;
        changed_by_name: string | null;
      }>;
      since_hours: number;
    }>(`/v1/activity/recent${s ? `?${s}` : ''}`);
  },
  listSyncRuns: () => request<{ items: SyncRun[] }>('/v1/sync-runs'),
  triggerSync: (input: { since_date?: string; set_watermark?: boolean; segments?: Segment[] }) =>
    request<SyncTriggerResult>('/v1/sync-runs/trigger', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  syncSingleProject: (projectId: string) =>
    request<SyncTriggerResult>(`/v1/projects/${projectId}/sync-salesforce`, {
      method: 'POST',
    }),
  // --- Automatischer Sync: Zeitplan (Admin) ---
  getSyncSchedule: () => request<SyncScheduleState>('/v1/sync-runs/schedule'),
  putSyncSchedule: (schedule: SyncSchedule) =>
    request<SyncScheduleState>('/v1/sync-runs/schedule', {
      method: 'PUT',
      body: JSON.stringify(schedule),
    }),
  // Delta-Wasserzeichen setzen ("ab Datum", YYYY-MM-DD) oder löschen (null).
  setSyncWatermark: (deltaWatermark: string | null) =>
    request<SyncScheduleState>('/v1/sync-runs/watermark', {
      method: 'PUT',
      body: JSON.stringify({ delta_watermark: deltaWatermark }),
    }),
  // --- Manuelles Re-Sync per Salesforce-Id (Admin + kaufm. Leitung) ---
  resyncByIds: (salesforceIds: string[]) =>
    request<SyncTriggerResult>('/v1/sync-runs/resync', {
      method: 'POST',
      body: JSON.stringify({ salesforce_ids: salesforceIds }),
    }),

  // --- Planner-Import (PV-009): Preview + Commit + History ---
  previewPlannerImport: (file: File): Promise<PlannerImportPreviewResponse> => {
    const form = new FormData();
    form.set('file', file);
    // Multipart upload — must NOT set Content-Type (browser sets boundary automatically)
    const sessionUserId = getSessionUserId();
    const headers: Record<string, string> = {};
    if (sessionUserId) headers['x-dev-user-id'] = sessionUserId;
    return fetch(`${BASE}/v1/planner-import/preview`, {
      method: 'POST',
      body: form,
      headers,
      credentials: 'include',
    }).then(async (res) => {
      if (!res.ok) {
        let detail = res.statusText;
        let payload: unknown = null;
        try {
          payload = await res.json();
          const b = payload as { error?: string; message?: string };
          detail = b.message ?? b.error ?? detail;
        } catch {
          // ignore non-JSON body
        }
        throw new ApiError(detail || `API ${res.status}`, res.status, payload);
      }
      return res.json() as Promise<PlannerImportPreviewResponse>;
    });
  },
  commitPlannerImport: (runId: string) =>
    request<PlannerImportCommitResponse>('/v1/planner-import/commit', {
      method: 'POST',
      body: JSON.stringify({ run_id: runId }),
    }),
  listPlannerImportRuns: () =>
    request<{ runs: PlannerImportRunSummary[] }>('/v1/planner-import/runs'),
  getPlannerImportRun: (runId: string) =>
    request<PlannerImportRunDetail>(`/v1/planner-import/runs/${runId}`),

  // --- Reihenfolge des Übergabe-Eingangs (#115) ---
  getUebergabeReihenfolge: () => request<{ items: string[] }>('/v1/uebergabe/reihenfolge'),
  verschiebeUebergabeReihenfolge: (body: {
    project_id: string;
    before_project_id: string | null;
  }) =>
    request<{ items: string[] }>('/v1/uebergabe/reihenfolge/verschieben', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  resetUebergabeReihenfolge: () =>
    request<{ items: string[] }>('/v1/uebergabe/reihenfolge', { method: 'DELETE' }),

  completeProject: (projectId: string, reason?: string) =>
    request<Project>(`/v1/projects/${projectId}/complete`, {
      method: 'POST',
      body: JSON.stringify(reason ? { reason } : {}),
    }),
  reactivateProject: (projectId: string, reason?: string) =>
    request<Project>(`/v1/projects/${projectId}/reactivate`, {
      method: 'POST',
      body: JSON.stringify(reason ? { reason } : {}),
    }),
  setProjectBlocked: (projectId: string, reason: string) =>
    request<Project>(`/v1/projects/${projectId}/blocked`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),
  unblockProject: (projectId: string, reason?: string) =>
    request<Project>(`/v1/projects/${projectId}/unblocked`, {
      method: 'POST',
      body: JSON.stringify(reason ? { reason } : {}),
    }),
  flagQsWithBl: (projectId: string, reason?: string) =>
    request<Project>(`/v1/projects/${projectId}/qs-bl-flag`, {
      method: 'POST',
      body: JSON.stringify(reason ? { reason } : {}),
    }),
  resolveQsWithBl: (projectId: string, reason?: string) =>
    request<Project>(`/v1/projects/${projectId}/qs-bl-resolve`, {
      method: 'POST',
      body: JSON.stringify(reason ? { reason } : {}),
    }),
  testSalesforce: () =>
    request<SfTestResult>('/v1/admin/salesforce/test').catch((err) => ({
      ok: false as const,
      reason: err instanceof Error ? err.message : 'unknown',
    })),
  describeSfObject: (object?: string) =>
    request<{
      name: string;
      label: string;
      fields: { name: string; label: string; type: string; custom: boolean }[];
    }>(`/v1/admin/salesforce/describe${object ? `?object=${encodeURIComponent(object)}` : ''}`),
  // --- Staffing comments ---
  listStaffingComments: (projectId: string, opts?: { staffingOnly?: boolean }) =>
    request<{ items: StaffingComment[] }>(
      `/v1/staffing/projects/${projectId}/comments${opts?.staffingOnly ? '?staffing=true' : ''}`,
    ),
  createStaffingComment: (
    projectId: string,
    input: { body: string; mentions?: string[]; is_staffing: boolean },
  ) =>
    request<StaffingComment>(`/v1/staffing/projects/${projectId}/comments`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  // #162: nur der Autor, nur Staffing-Kommentare; Erwähnungen leitet der Server aus dem body ab.
  updateStaffingComment: (commentId: string, input: { body: string }) =>
    request<StaffingComment>(`/v1/staffing/comments/${commentId}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    }),
  deleteStaffingComment: (commentId: string) =>
    request<{ deleted: string }>(`/v1/staffing/comments/${commentId}`, {
      method: 'DELETE',
    }),
  // --- Besetzung: Ist (SF) + Soll (beauftragte Positionen) + Abgleich (PV-032/033) ---
  // Lesen ist bewusst nicht rechte-gated (Spec Abschnitt 5), Schreiben laeuft
  // serverseitig ueber `staffing_manage`.
  getBesetzung: (projectId: string) =>
    request<BesetzungView>(`/v1/besetzung/projects/${projectId}`),
  createBeauftragtePosition: (
    projectId: string,
    input: {
      stufe: string;
      preis?: number;
      preis_einheit?: PreisEinheit;
      anzahl_pt?: number;
      notiz?: string | null;
    },
  ) =>
    request<{ item: BeauftragtePosition }>(`/v1/besetzung/projects/${projectId}/positionen`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  patchBeauftragtePosition: (
    positionId: string,
    input: {
      stufe?: string;
      preis?: number | null;
      preis_einheit?: PreisEinheit;
      anzahl_pt?: number | null;
      status?: 'ki_vorschlag' | 'bestaetigt';
      unsicher?: boolean;
      notiz?: string | null;
    },
  ) =>
    request<{ item: BeauftragtePosition }>(`/v1/besetzung/positionen/${positionId}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    }),
  deleteBeauftragtePosition: (positionId: string) =>
    request<{ ok: true }>(`/v1/besetzung/positionen/${positionId}`, { method: 'DELETE' }),
  extractBeauftragtePositionen: (projectId: string) =>
    request<{ created: number; replaced: number }>(`/v1/besetzung/projects/${projectId}/extract`, {
      method: 'POST',
    }),
  // --- Staffing-Maske (#158/#163): Positionen (= Bedarf), Besetzungen. Query-Key `['staffing-maske', projectId]`. ---
  getStaffingMaske: (projectId: string) =>
    request<StaffingMaske>(`/v1/staffing/projects/${projectId}/maske`),
  createStaffingBesetzung: (projectId: string, input: CreateStaffingBesetzung) =>
    request<{ item: StaffingBesetzung }>(`/v1/staffing/projects/${projectId}/besetzungen`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  // Sendet genau die uebergebenen Felder (nie ein Gesamtobjekt): der Service prueft jede
  // mitgeschickte `user_id` gegen die Person-/Nutzer-Regeln.
  patchStaffingBesetzung: (besetzungId: string, input: PatchStaffingBesetzung) =>
    request<{ item: StaffingBesetzung }>(`/v1/staffing/besetzungen/${besetzungId}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    }),
  deleteStaffingBesetzung: (besetzungId: string) =>
    request<undefined>(`/v1/staffing/besetzungen/${besetzungId}`, { method: 'DELETE' }),
  // Staffing neu (Issue #1): kuratierte KL-Population.
  getKlStaffingBoard: (segment?: string) =>
    request<KlStaffingBoardResponse>(
      `/v1/staffing/kl-board${segment ? `?segment=${encodeURIComponent(segment)}` : ''}`,
    ),
  // KFML-Worklist (PV-016): kuratierte Liste der manuell angehakten Projekte.
  getKfmlWorklist: () => request<KfmlWorklistResponse>('/v1/staffing/kfml-worklist'),
  // --- Segment-Notizen (#32) ---
  listSegmentNotes: (segment: string, opts?: { includeDone?: boolean }) =>
    request<{ items: SegmentNote[] }>(
      `/v1/segment-notes?segment=${encodeURIComponent(segment)}${
        opts?.includeDone ? '&include_done=true' : ''
      }`,
    ),
  createSegmentNote: (input: { segment: string; body: string; kfml_worklist?: boolean }) =>
    request<SegmentNote>('/v1/segment-notes', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  patchSegmentNote: (id: string, input: { done?: boolean; kfml_worklist?: boolean }) =>
    request<SegmentNote>(`/v1/segment-notes/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    }),
  deleteSegmentNote: (id: string) =>
    request<{ deleted: string }>(`/v1/segment-notes/${id}`, {
      method: 'DELETE',
    }),
  // Bild-Upload für Segment-Notizen (multipart). Gibt die browser-ladbare URL zurück
  // (BASE vorangestellt), die als Markdown `![...](url)` in den Notiz-Text eingefügt wird.
  uploadSegmentNoteImage: (segment: string, file: File): Promise<SegmentNoteImageUpload> => {
    const body = new FormData();
    body.append('file', file);
    body.append('segment', segment);
    const sessionUserId = getSessionUserId();
    const headers: Record<string, string> = {};
    if (sessionUserId) headers['x-dev-user-id'] = sessionUserId;
    return fetch(`${BASE}/v1/segment-notes/images`, {
      method: 'POST',
      body,
      headers,
      credentials: 'include',
    }).then(async (res) => {
      if (!res.ok) {
        let detail = res.statusText;
        try {
          const payload = (await res.json()) as { message?: string };
          if (payload?.message) detail = payload.message;
        } catch {
          // kein JSON-Body — statusText belassen
        }
        throw new Error(detail);
      }
      const data = (await res.json()) as SegmentNoteImageUpload;
      // Server liefert API-relative URL (/v1/...); für <img>/Editor die BASE voranstellen.
      return { image_id: data.image_id, url: `${BASE}${data.url}` };
    });
  },
  // „Staffing abgeschlossen" setzen / wieder öffnen (Staffing neu, Issue #1).
  completeStaffing: (projectId: string) =>
    request<Project>(`/v1/staffing/projects/${projectId}/complete`, { method: 'POST' }),
  reopenStaffing: (projectId: string) =>
    request<Project>(`/v1/staffing/projects/${projectId}/reopen`, { method: 'POST' }),
  // --- KI Mission Briefings ---
  listKiBriefings: (projectId: string) =>
    request<{ items: KiBriefing[] }>(`/v1/ki/projects/${projectId}/briefings`),
  // V1.5: alle Briefings cross-project (Briefings-Library)
  listAllKiBriefings: () =>
    request<{ items: Array<KiBriefing & { project_key: string; customer_name: string }> }>(
      '/v1/ki/briefings',
    ),
  generateKiBriefing: (projectId: string) =>
    request<KiBriefing>(`/v1/ki/projects/${projectId}/briefings`, { method: 'POST' }),
  patchKiBriefing: (briefingId: string, input: { body: string }) =>
    request<KiBriefing>(`/v1/ki/briefings/${briefingId}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    }),
  deleteKiBriefing: (briefingId: string) =>
    request<{ deleted: string }>(`/v1/ki/briefings/${briefingId}`, { method: 'DELETE' }),

  // --- KFML-Todoliste (Issue #14, WP-7): on-demand To-do-Liste je Segment ---
  kfmlTodos: (segment: string) =>
    request<KfmlTodoList>('/v1/kfml-todos', {
      method: 'POST',
      body: JSON.stringify({ segment }),
    }),

  // --- MB-A: Attachments ---
  listAttachments: (projectId: string) =>
    request<{
      items: ProjectAttachment[];
    }>(`/v1/projects/${projectId}/attachments`),

  uploadAttachment: (projectId: string, file: File) => {
    const body = new FormData();
    body.append('file', file);
    // Multipart upload — override Content-Type (browser sets boundary automatically)
    const sessionUserId = getSessionUserId();
    const headers: Record<string, string> = {};
    if (sessionUserId) headers['x-dev-user-id'] = sessionUserId;
    return fetch(`${BASE}/v1/projects/${projectId}/attachments`, {
      method: 'POST',
      body,
      headers,
      credentials: 'include',
    }).then(async (res) => {
      if (!res.ok) {
        let detail = res.statusText;
        let payload: unknown = null;
        try {
          payload = await res.json();
          const b = payload as { error?: string; message?: string };
          detail = b.message ?? b.error ?? detail;
        } catch {
          // ignore
        }
        throw new ApiError(detail || `API ${res.status}`, res.status, payload);
      }
      return res.json() as Promise<ProjectAttachment>;
    });
  },

  deleteAttachment: (attachmentId: string) =>
    request<{ deleted: string }>(`/v1/attachments/${attachmentId}`, { method: 'DELETE' }),

  // Inline-Ansicht (PDF/Bilder) im neuen Tab statt Download.
  getAttachmentViewUrl: (attachmentId: string) =>
    `${BASE}/v1/attachments/${attachmentId}/download?disposition=inline`,

  getAttachmentDownloadUrl: (attachmentId: string) =>
    `${BASE}/v1/attachments/${attachmentId}/download`,

  // --- MB-A: Salesforce-Dateien ---
  listSfFiles: (projectId: string) =>
    request<{ items: SfFileListItem[] }>(`/v1/projects/${projectId}/sf-files`),

  importSfFiles: (projectId: string, contentVersionIds: string[]) =>
    request<{ imported: number; items: Array<{ attachment_id: string; filename: string }> }>(
      `/v1/projects/${projectId}/sf-files/import`,
      { method: 'POST', body: JSON.stringify({ content_version_ids: contentVersionIds }) },
    ),

  // --- Dev-Login (Phase G Stub) ---
  listDevUsers: () => request<{ items: DevUser[]; mode: 'dev-stub' }>('/v1/auth/dev-users'),
  // --- Admin: User-Verwaltung & Funktionsrollen ---
  adminListUsers: () => request<{ items: AdminUser[] }>('/v1/admin/users'),
  adminCreateUser: (input: {
    email: string;
    name: string;
    /** @deprecated System-Rolle; DB-Default 'reader' greift. Entfernen Release 2. */
    role?: 'reader' | 'editor' | 'admin';
    is_test_user?: boolean;
  }) => request<User>('/v1/admin/users', { method: 'POST', body: JSON.stringify(input) }),
  adminUpdateUser: (
    id: string,
    input: {
      email?: string;
      name?: string;
      /** @deprecated System-Rolle. Entfernen Release 2. */
      role?: 'reader' | 'editor' | 'admin';
      /** Admin-Recht (einachsig, separat vergebbar). */
      is_admin?: boolean;
      is_test_user?: boolean;
      /** #116: false = deaktivieren (Rollen + Admin-Recht weg), true = reaktivieren („Neue Nutzer"). */
      active?: boolean;
    },
  ) =>
    request<User>(`/v1/admin/users/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    }),
  adminDeleteUser: (id: string) =>
    request<{ deleted: string }>(`/v1/admin/users/${id}`, { method: 'DELETE' }),
  adminAssignFunctionRole: (
    userId: string,
    input: { role: FunctionRole; scope_segment?: Segment | null },
  ) =>
    request<{
      user_function_role_id: string;
      user_id: string;
      role: FunctionRole;
      scope_segment: Segment | null;
      created_at: string;
    }>(`/v1/admin/users/${userId}/function-roles`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  adminRemoveFunctionRole: (functionRoleId: string) =>
    request<{ deleted: string }>(`/v1/admin/function-roles/${functionRoleId}`, {
      method: 'DELETE',
    }),

  // --- Tags / Custom Labels pro Bereich ---
  listTags: (segment?: Segment | 'global') =>
    request<{ items: Tag[] }>(
      `/v1/tags${segment ? `?scope_segment=${encodeURIComponent(segment)}` : ''}`,
    ),
  createTag: (input: {
    name: string;
    color?: string | null;
    scope_segment?: Segment | null;
    description?: string | null;
    manager_user_id?: string | null;
  }) => request<Tag>('/v1/tags', { method: 'POST', body: JSON.stringify(input) }),
  updateTag: (
    id: string,
    input: {
      name?: string;
      color?: string | null;
      scope_segment?: Segment | null;
      description?: string | null;
      manager_user_id?: string | null;
    },
  ) => request<Tag>(`/v1/tags/${id}`, { method: 'PATCH', body: JSON.stringify(input) }),
  deleteTag: (id: string) => request<{ deleted: string }>(`/v1/tags/${id}`, { method: 'DELETE' }),
  listProjectTags: (projectId: string) =>
    request<{ items: Tag[] }>(`/v1/tags/projects/${projectId}`),
  attachTag: (projectId: string, tagId: string) =>
    request<{ project_id: string; tag_id: string }>(`/v1/tags/projects/${projectId}/${tagId}`, {
      method: 'POST',
    }),
  detachTag: (projectId: string, tagId: string) =>
    request<{ project_id: string; tag_id: string; deleted: true }>(
      `/v1/tags/projects/${projectId}/${tagId}`,
      { method: 'DELETE' },
    ),

  // --- Globale Suche (F10) ---
  searchProjects: (q: string) =>
    request<{
      items: Array<{
        project_id: string;
        project_key: string;
        name: string;
        customer_name: string;
        segment: string;
      }>;
    }>(`/v1/projects/search?q=${encodeURIComponent(q)}`),
  resolveProjectBySfId: (sfId: string) =>
    request<{ project_id: string }>(`/v1/projects/resolve/${encodeURIComponent(sfId)}`),

  // --- Auth (F11, Muster A) ---
  authMe: () =>
    request<{
      user_id: string;
      email: string;
      name: string;
      is_admin: boolean;
      is_test_user: boolean;
      function_roles: { role: FunctionRole; scope_segment: Segment | null }[];
      mode: 'azure-ad';
    }>('/v1/auth/me'),
  logout: () => request<{ ok: true }>('/v1/auth/logout', { method: 'POST' }),

  // --- Feature-Flags (F1) ---
  listFeatureFlags: () => request<{ items: FeatureFlag[] }>('/v1/feature-flags'),
  setFeatureFlag: (key: string, enabled: boolean) =>
    request<FeatureFlag>(`/v1/feature-flags/${key}`, {
      method: 'PATCH',
      body: JSON.stringify({ enabled }),
    }),

  // --- Systemprompts (PV-003, admin) ---
  listPrompts: () => request<{ items: PromptListItem[] }>('/v1/admin/prompts'),
  updatePrompt: (key: string, content: string) =>
    request<PromptListItem>(`/v1/admin/prompts/${encodeURIComponent(key)}`, {
      method: 'PUT',
      body: JSON.stringify({ content }),
    }),
  resetPrompt: (key: string) =>
    request<PromptListItem>(`/v1/admin/prompts/${encodeURIComponent(key)}/reset`, {
      method: 'POST',
    }),

  // --- MB-C: MCP-Verbindung (Das Orakel) ---
  getMcpStatus: () =>
    request<{
      status: McpStatusValue;
      subject?: string;
      connected_at?: string;
    }>('/v1/mcp/status'),
  testMcpConnection: () =>
    request<{ ok: boolean; tools?: number }>('/v1/mcp/test', { method: 'POST' }),
  disconnectMcp: () => request<{ ok: boolean }>('/v1/mcp/connection', { method: 'DELETE' }),
  /** Orakel-Login starten; `next` = Rücksprungziel (PV-046), die API prüft es. */
  getMcpConnectUrl: (next?: string) =>
    `${BASE}/v1/mcp/connect${next ? `?next=${encodeURIComponent(next)}` : ''}`,

  // --- Admin: pflegbare Mail-Benachrichtigungen (#11 WP-6/7) ---
  listNotificationTemplates: () =>
    request<{ items: NotificationTemplateListItem[] }>('/v1/admin/notifications/templates'),
  getNotificationTemplate: (key: string, locale: NotificationLocale) =>
    request<NotificationTemplateDetail>(
      `/v1/admin/notifications/templates/${encodeURIComponent(key)}/${locale}`,
    ),
  updateNotificationTemplate: (
    key: string,
    locale: NotificationLocale,
    body: NotificationTemplateInput,
  ) =>
    request<{ ok: boolean; template_id?: string }>(
      `/v1/admin/notifications/templates/${encodeURIComponent(key)}/${locale}`,
      { method: 'PUT', body: JSON.stringify(body) },
    ),
  resetNotificationTemplate: (key: string, locale: NotificationLocale) =>
    request<{ ok: boolean; is_overridden: boolean }>(
      `/v1/admin/notifications/templates/${encodeURIComponent(key)}/${locale}/reset`,
      { method: 'POST' },
    ),
  previewNotificationTemplate: (
    key: string,
    locale: NotificationLocale,
    payload?: Partial<NotificationTemplateInput>,
  ) =>
    request<{ subject: string; html: string; text: string }>(
      `/v1/admin/notifications/templates/${encodeURIComponent(key)}/${locale}/preview`,
      { method: 'POST', body: JSON.stringify(payload ?? {}) },
    ),
  testSendNotificationTemplate: (key: string, locale: NotificationLocale) =>
    request<{ ok: boolean }>(
      `/v1/admin/notifications/templates/${encodeURIComponent(key)}/${locale}/test-send`,
      { method: 'POST' },
    ),
  listNotificationRules: () =>
    request<{ items: NotificationRuleListItem[] }>('/v1/admin/notifications/rules'),
  updateNotificationRule: (eventType: NotificationEventType, body: NotificationRuleInput) =>
    request<{ ok: boolean; rule_id?: string }>(`/v1/admin/notifications/rules/${eventType}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),
  getDigestSettings: () => request<DigestSettings>('/v1/admin/notifications/digest-settings'),
  updateDigestSettings: (body: DigestSettingsInput) =>
    request<DigestSettings>('/v1/admin/notifications/digest-settings', {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  // --- Handlungshinweise (Spec 2026-09-16, §3.6) ---
  listActionTexts: () =>
    request<{ items: ActionTextListItem[] }>('/v1/admin/notifications/actions'),
  updateActionText: (kind: ActionKind, locale: NotificationLocale, body: ActionTextInput) =>
    request<{ ok: boolean }>(`/v1/admin/notifications/actions/${kind}/${locale}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),
  resetActionText: (kind: ActionKind, locale: NotificationLocale) =>
    request<{ ok: boolean }>(`/v1/admin/notifications/actions/${kind}/${locale}/reset`, {
      method: 'POST',
    }),
  getActionMatrix: (locale: NotificationLocale) =>
    request<{ locale: NotificationLocale; items: ActionTextMatrix }>(
      `/v1/notifications/actions?locale=${locale}`,
    ),

  // --- Projekt-Befunde (PV-052) ---
  listBefundeStapel: (locale: BefundLocale) =>
    request<BefundListe>(`/v1/befunde?sicht=stapel&locale=${locale}`),
  listBefundeAufsicht: (locale: BefundLocale) =>
    request<BefundAufsicht>(`/v1/befunde?sicht=aufsicht&locale=${locale}`),
  listBefundeErledigt: (locale: BefundLocale) =>
    request<BefundErledigtListe>(`/v1/befunde?sicht=erledigt&locale=${locale}`),
  listBefundeProjekt: (projectId: string, locale: BefundLocale) =>
    request<BefundListe>(`/v1/befunde/projekt/${projectId}?locale=${locale}`),
  getBefundUebersicht: (locale: BefundLocale) =>
    request<BefundUebersicht>(`/v1/befunde/uebersicht?locale=${locale}`),
  getBefundVerlauf: (befundId: string) => request<BefundVerlauf>(`/v1/befunde/${befundId}/verlauf`),
  befundAktion: (befundId: string, body: BefundAktionInput) =>
    request<{ ok: boolean }>(`/v1/befunde/${befundId}/aktion`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  befundNeuPruefen: (projectId: string) =>
    request<{ ok: boolean; neu: number; erledigt: number; float_ok: boolean | null }>(
      '/v1/befunde/pruefen',
      { method: 'POST', body: JSON.stringify({ project_id: projectId }) },
    ),
  getBefundEinstellungen: () =>
    request<BefundPruefungEinstellungen>('/v1/admin/befunde/einstellungen'),
  updateBefundEinstellungen: (body: UpdateBefundPruefungEinstellungenInput) =>
    request<BefundPruefungEinstellungen>('/v1/admin/befunde/einstellungen', {
      method: 'PUT',
      body: JSON.stringify(body),
    }),
  listBefundRegeln: (locale: BefundLocale) =>
    request<{ items: BefundRegelAdmin[] }>(`/v1/admin/befunde/regeln?locale=${locale}`),
  setBefundRegelAktiv: (regel: BefundRegelKey, aktiv: boolean) =>
    request<{ ok: boolean }>(`/v1/admin/befunde/regeln/${regel}`, {
      method: 'PUT',
      body: JSON.stringify({ aktiv }),
    }),
  starteBefundLauf: () =>
    request<{ status: 'gestartet' }>('/v1/admin/befunde/lauf', { method: 'POST' }),

  // --- Neue Nutzer + Entra-Abgleich (#116) ---
  listNeueNutzer: (erledigte: boolean) =>
    request<{ items: NeuerNutzer[] }>(`/v1/admin/neue-nutzer${erledigte ? '?erledigte=1' : ''}`),
  setzeNeuGeprueft: (userId: string) =>
    request<undefined>(`/v1/admin/neue-nutzer/${userId}/geprueft`, { method: 'POST' }),
  nimmNeuGeprueftZurueck: (userId: string) =>
    request<undefined>(`/v1/admin/neue-nutzer/${userId}/geprueft`, { method: 'DELETE' }),
  getEntraAbgleich: () => request<EntraAbgleichEinstellungen>('/v1/admin/neue-nutzer/abgleich'),
  putEntraZeitplan: (input: UpdateEntraAbgleichZeitplanInput) =>
    request<EntraAbgleichEinstellungen>('/v1/admin/neue-nutzer/abgleich', {
      method: 'PUT',
      body: JSON.stringify(input),
    }),
  /** `probelauf` = alles berechnen, nichts speichern (außer dem Ergebnis). */
  starteEntraAbgleich: (probelauf = false) =>
    request<{ status: 'gestartet' }>('/v1/admin/neue-nutzer/abgleich/lauf', {
      method: 'POST',
      body: JSON.stringify({ probelauf }),
    }),

  // --- Hilfe (PV-037) ---
  getHilfeDatenstand: () => request<Datenstand>('/v1/hilfe/datenstand'),
  getHilfeBefundRegeln: (locale: BefundLocale) =>
    request<BefundRegelnHilfe>(`/v1/hilfe/befund-regeln?locale=${locale}`),
  listNews: () => request<NewsListeResponse>('/v1/news'),
  markNewsGelesen: () => request<NewsGelesenResponse>('/v1/news/gelesen', { method: 'POST' }),

  // --- Produkt-News, Admin (PV-037) ---
  listAdminNews: () => request<{ items: ProduktNews[] }>('/v1/admin/news'),
  createNews: (body: NewsEntwurf) =>
    request<ProduktNews>('/v1/admin/news', { method: 'POST', body: JSON.stringify(body) }),
  updateNews: (id: string, body: NewsEntwurf) =>
    request<ProduktNews>(`/v1/admin/news/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  veroeffentlicheNews: (id: string) =>
    request<ProduktNews>(`/v1/admin/news/${id}/veroeffentlichen`, { method: 'POST' }),
  zieheNewsZurueck: (id: string) =>
    request<ProduktNews>(`/v1/admin/news/${id}/zurueckziehen`, { method: 'POST' }),
  deleteNews: (id: string) =>
    request<{ ok: boolean }>(`/v1/admin/news/${id}`, { method: 'DELETE' }),
  uebersetzeNews: (body: NewsUebersetzenRequest) =>
    request<NewsUebersetzenResponse>('/v1/admin/news/uebersetzen', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  // --- Float-Verbindung (PV-052 L4) ---
  getFloatDienst: () => request<DienstStatus>('/v1/admin/dienste/float'),
  testeFloatDienst: () =>
    request<DienstTestErgebnis>('/v1/admin/dienste/float/test', { method: 'POST' }),
  trenneFloatDienst: () =>
    request<{ ok: boolean }>('/v1/admin/dienste/float', { method: 'DELETE' }),
  /** Volle Navigation (kein fetch): der Login läuft über den Browser. */
  floatConnectUrl: () => `${BASE}/v1/admin/dienste/float/connect`,
};

// --- Admin-Notifications: Response-/Input-Typen (#11 WP-6/7) ---
export type NotificationTemplateListItem = {
  key: string;
  locale: NotificationLocale;
  is_overridden: boolean;
  subject: string;
  intro_body: string;
  cta_label: string;
  footer: string;
  digest_summary: string | null;
  is_active: boolean;
  updated_at: string | null;
  placeholders: string[];
};

export type NotificationTemplateDetail = {
  key: string;
  locale: NotificationLocale;
  is_overridden: boolean;
  current: {
    subject: string;
    intro_body: string;
    cta_label: string;
    footer: string;
    digest_summary: string | null;
  };
  default: {
    subject: string;
    intro_body: string;
    cta_label: string;
    footer: string;
    digest_summary: string | null;
  };
  placeholders: string[];
  is_active: boolean;
  updated_at: string | null;
};

export type NotificationTemplateInput = {
  subject: string;
  intro_body: string;
  cta_label: string;
  footer: string;
  digest_summary?: string | null;
};

export type NotificationRuleListItem = {
  event_type: NotificationEventType;
  channel: NotificationChannel;
  cadence: NotificationCadence;
  first_immediate: boolean;
  recipients: NotificationRecipientRole[];
  recipient_strategy: string | null;
  template_key: string;
  is_active: boolean;
  updated_at: string;
};

export type NotificationRuleInput = {
  channel: NotificationChannel;
  cadence: NotificationCadence;
  first_immediate: boolean;
  recipients: NotificationRecipientRole[];
  template_key: string;
  is_active: boolean;
};

// --- Digest-Versand-Einstellungen (#20) ---
export type DigestSettingsInput = {
  enabled: boolean;
  /** HH:MM (24h) in der Server-Zeitzone. */
  time: string;
  /** Versand-Wochentage nach JS Date.getDay(): 0=So … 6=Sa. */
  weekdays: number[];
};

export type DigestSettings = DigestSettingsInput & {
  timezone: string;
  updated_at: string | null;
  updated_by: string | null;
};

// --- Handlungshinweise (Spec 2026-09-16, §3.6) ---
export type ActionTextFields = {
  label: string | null;
  action_overdue: string | null;
  action_due_soon: string | null;
};
export type ActionTextListItem = ActionTextFields & {
  kind: ActionKind;
  locale: NotificationLocale;
  is_overridden: boolean;
  default: ActionTextFields;
  updated_at: string | null;
};
export type ActionTextInput = ActionTextFields;
export type ActionTextMatrix = Record<ActionKind, ActionTextFields>;
